import React, { useState, useEffect } from 'react';
import { Activity, Heart, Clock, Dumbbell, Info, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Exercise Vital Sign Section
 * Based on WHO Physical Activity Guidelines
 * Includes education, auto-calculation, and audit-ready data
 */
const ExerciseVitalSignSection = ({ config, formData, onChange }) => {
  const [showEducation, setShowEducation] = useState(false);
  const [whoResult, setWhoResult] = useState(null);

  // Calculate WHO guideline adherence whenever relevant fields change
  useEffect(() => {
    calculateWHOAdherence();
  }, [
    formData.exerciseDaysPerWeek,
    formData.exerciseMinutesPerDay,
    formData.exerciseIntensity,
    formData.strengthTrainingDays,
    formData.dateOfBirth
  ]);

  const calculateWHOAdherence = () => {
    const days = parseInt(formData.exerciseDaysPerWeek) || 0;
    const minutes = getMinutesValue(formData.exerciseMinutesPerDay);
    const intensity = formData.exerciseIntensity;
    const strengthDays = formData.strengthTrainingDays;

    if (!formData.exerciseDaysPerWeek || !formData.exerciseMinutesPerDay) {
      setWhoResult(null);
      return;
    }

    // Calculate weekly minutes
    const weeklyMinutes = days * minutes;

    // Determine if adult or child (use 18 as cutoff)
    const isAdult = !formData.dateOfBirth || calculateAge(formData.dateOfBirth) >= 18;

    let meetsGuideline = false;
    let summary = {};

    if (isAdult) {
      // Adult WHO Guidelines:
      // - 150-300 min/week moderate OR 75-150 min/week vigorous
      // - Strength training 2+ days/week
      const meetsAerobic = (
        (intensity === 'moderate' && weeklyMinutes >= 150) ||
        (intensity === 'vigorous' && weeklyMinutes >= 75) ||
        (intensity === 'light' && weeklyMinutes >= 300) // Light needs more
      );
      const meetsStrength = strengthDays === 'yes_2plus';
      meetsGuideline = meetsAerobic && meetsStrength;

      summary = {
        isAdult: true,
        weeklyMinutes,
        intensity,
        meetsAerobic,
        meetsStrength,
        meetsGuideline,
        recommendation: meetsGuideline 
          ? 'Great! You are meeting the WHO physical activity guidelines.'
          : getAdultRecommendation(weeklyMinutes, intensity, strengthDays)
      };
    } else {
      // Child/Adolescent WHO Guidelines (5-17 years):
      // - Average 60+ min/day moderate-vigorous
      // - Strength/bone activities 3+ days/week
      const dailyMinutes = weeklyMinutes / 7;
      const meetsAerobic = dailyMinutes >= 60 && (intensity === 'moderate' || intensity === 'vigorous');
      const meetsStrength = strengthDays === 'yes_2plus' || strengthDays === 'yes_1day';
      meetsGuideline = meetsAerobic;

      summary = {
        isAdult: false,
        dailyMinutes: Math.round(dailyMinutes),
        weeklyMinutes,
        intensity,
        meetsAerobic,
        meetsStrength,
        meetsGuideline,
        recommendation: meetsGuideline
          ? 'Great! You are meeting the WHO physical activity guidelines for young people.'
          : getChildRecommendation(dailyMinutes, intensity)
      };
    }

    setWhoResult(summary);
    
    // Store the calculated values for auditing
    onChange('evsWeeklyMinutes', weeklyMinutes);
    onChange('evsMeetsWHOGuideline', meetsGuideline ? 'Yes' : 'No');
  };

  const calculateAge = (dob) => {
    if (!dob) return 30; // Default to adult
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getMinutesValue = (selection) => {
    switch (selection) {
      case 'less_than_10': return 5;
      case '10_20': return 15;
      case '20_30': return 25;
      case '30_60': return 45;
      case 'more_than_60': return 75;
      default: return 0;
    }
  };

  const getAdultRecommendation = (weeklyMinutes, intensity, strengthDays) => {
    const recommendations = [];
    
    if (intensity === 'moderate' && weeklyMinutes < 150) {
      recommendations.push(`Try to increase to at least 150 minutes of moderate activity per week (you're currently at ${weeklyMinutes} minutes).`);
    } else if (intensity === 'vigorous' && weeklyMinutes < 75) {
      recommendations.push(`Try to increase to at least 75 minutes of vigorous activity per week (you're currently at ${weeklyMinutes} minutes).`);
    } else if (intensity === 'light') {
      recommendations.push('Try to increase the intensity of your activity to moderate level (where you can talk but not sing).');
    }
    
    if (strengthDays !== 'yes_2plus') {
      recommendations.push('Add muscle-strengthening activities at least 2 days per week.');
    }
    
    return recommendations.join(' ');
  };

  const getChildRecommendation = (dailyMinutes, intensity) => {
    const recommendations = [];
    
    if (dailyMinutes < 60) {
      recommendations.push(`Try to be active for at least 60 minutes each day (you're averaging ${Math.round(dailyMinutes)} minutes).`);
    }
    
    if (intensity === 'light') {
      recommendations.push('Try to include more activities that make you breathe harder and get your heart pumping.');
    }
    
    return recommendations.join(' ');
  };

  const handleChange = (field, value) => {
    onChange(field, value);
  };

  const radioStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'all 0.2s'
  };

  const radioStyleSelected = {
    ...radioStyle,
    backgroundColor: '#eff6ff',
    borderColor: '#3b82f6'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '15px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '12px'
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Introduction Banner */}
      <div style={{
        backgroundColor: '#ecfdf5',
        border: '1px solid #a7f3d0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Activity style={{ width: '24px', height: '24px', color: '#059669', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h3 style={{ margin: '0 0 8px 0', color: '#065f46', fontSize: '16px' }}>
              Why we ask everyone about exercise
            </h3>
            <p style={{ margin: 0, color: '#047857', fontSize: '14px', lineHeight: '1.5' }}>
              The World Health Organization says physical activity is one of the best ways to stay healthy. 
              It helps your body, your mind, and your joints. We ask everyone about exercise, just like we 
              ask about blood pressure or weight.
            </p>
          </div>
        </div>
      </div>

      {/* Question 1: Days per week */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          <Heart style={{ width: '18px', height: '18px', display: 'inline', marginRight: '8px', verticalAlign: 'text-bottom', color: '#ef4444' }} />
          On how many days each week are you physically active?
        </label>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', marginTop: '-4px' }}>
          Physical activity means moving your body and using energy – like walking, sport, cycling, swimming, dancing, or active play.
        </p>
        {['0', '1-2', '3-4', '5-7'].map(option => (
          <label
            key={option}
            style={formData.exerciseDaysPerWeek === option ? radioStyleSelected : radioStyle}
          >
            <input
              type="radio"
              name="exerciseDaysPerWeek"
              value={option}
              checked={formData.exerciseDaysPerWeek === option}
              onChange={(e) => handleChange('exerciseDaysPerWeek', e.target.value)}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <span>{option === '0' ? '0 days' : `${option} days`}</span>
          </label>
        ))}
      </div>

      {/* Question 2: Minutes per day */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          <Clock style={{ width: '18px', height: '18px', display: 'inline', marginRight: '8px', verticalAlign: 'text-bottom', color: '#3b82f6' }} />
          On those days, how long are you active for?
        </label>
        {[
          { value: 'less_than_10', label: 'Less than 10 minutes' },
          { value: '10_20', label: '10-20 minutes' },
          { value: '20_30', label: '20-30 minutes' },
          { value: '30_60', label: '30-60 minutes' },
          { value: 'more_than_60', label: 'More than 60 minutes' }
        ].map(option => (
          <label
            key={option.value}
            style={formData.exerciseMinutesPerDay === option.value ? radioStyleSelected : radioStyle}
          >
            <input
              type="radio"
              name="exerciseMinutesPerDay"
              value={option.value}
              checked={formData.exerciseMinutesPerDay === option.value}
              onChange={(e) => handleChange('exerciseMinutesPerDay', e.target.value)}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {/* Question 3: Intensity */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          How hard does it usually feel?
        </label>
        {[
          { value: 'light', label: 'Light – easy, you can talk and sing', color: '#22c55e' },
          { value: 'moderate', label: 'Moderate – you can talk but not sing', color: '#f59e0b' },
          { value: 'vigorous', label: 'Vigorous – talking is hard', color: '#ef4444' }
        ].map(option => (
          <label
            key={option.value}
            style={formData.exerciseIntensity === option.value ? radioStyleSelected : radioStyle}
          >
            <input
              type="radio"
              name="exerciseIntensity"
              value={option.value}
              checked={formData.exerciseIntensity === option.value}
              onChange={(e) => handleChange('exerciseIntensity', e.target.value)}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: option.color
              }}></span>
              {option.label}
            </span>
          </label>
        ))}
      </div>

      {/* Question 4: Strength Training */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          <Dumbbell style={{ width: '18px', height: '18px', display: 'inline', marginRight: '8px', verticalAlign: 'text-bottom', color: '#8b5cf6' }} />
          Do you do activities that make your muscles stronger?
        </label>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', marginTop: '-4px' }}>
          Examples: lifting weights, resistance bands, climbing, push-ups, squats, or other body-weight exercises.
        </p>
        {[
          { value: 'no', label: 'No' },
          { value: 'yes_1day', label: 'Yes – 1 day per week' },
          { value: 'yes_2plus', label: 'Yes – 2 or more days per week' }
        ].map(option => (
          <label
            key={option.value}
            style={formData.strengthTrainingDays === option.value ? radioStyleSelected : radioStyle}
          >
            <input
              type="radio"
              name="strengthTrainingDays"
              value={option.value}
              checked={formData.strengthTrainingDays === option.value}
              onChange={(e) => handleChange('strengthTrainingDays', e.target.value)}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {/* Question 5: Sitting Time */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          About how many hours a day do you spend sitting or lying down (not sleeping)?
        </label>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', marginTop: '-4px' }}>
          Examples: school, work, screens, gaming, driving.
        </p>
        {[
          { value: 'less_than_4', label: 'Less than 4 hours' },
          { value: '4_6', label: '4-6 hours' },
          { value: '6_8', label: '6-8 hours' },
          { value: 'more_than_8', label: 'More than 8 hours' }
        ].map(option => (
          <label
            key={option.value}
            style={formData.sittingHoursPerDay === option.value ? radioStyleSelected : radioStyle}
          >
            <input
              type="radio"
              name="sittingHoursPerDay"
              value={option.value}
              checked={formData.sittingHoursPerDay === option.value}
              onChange={(e) => handleChange('sittingHoursPerDay', e.target.value)}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {/* WHO Result Summary */}
      {whoResult && (
        <div style={{
          backgroundColor: whoResult.meetsGuideline ? '#f0fdf4' : '#fef3c7',
          border: `1px solid ${whoResult.meetsGuideline ? '#86efac' : '#fcd34d'}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {whoResult.meetsGuideline ? (
              <CheckCircle style={{ width: '24px', height: '24px', color: '#16a34a', flexShrink: 0 }} />
            ) : (
              <AlertCircle style={{ width: '24px', height: '24px', color: '#d97706', flexShrink: 0 }} />
            )}
            <div>
              <h4 style={{ 
                margin: '0 0 8px 0', 
                color: whoResult.meetsGuideline ? '#166534' : '#92400e',
                fontSize: '15px'
              }}>
                Your Activity Summary
              </h4>
              <p style={{ 
                margin: '0 0 8px 0', 
                color: whoResult.meetsGuideline ? '#15803d' : '#a16207',
                fontSize: '14px'
              }}>
                <strong>Estimated weekly activity:</strong> {whoResult.weeklyMinutes} minutes
                {whoResult.isAdult ? '' : ` (${whoResult.dailyMinutes} min/day average)`}
              </p>
              <p style={{ 
                margin: 0, 
                color: whoResult.meetsGuideline ? '#15803d' : '#a16207',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                {whoResult.recommendation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Education Toggle */}
      <button
        type="button"
        onClick={() => setShowEducation(!showEducation)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#f3f4f6',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          color: '#374151',
          width: '100%',
          marginBottom: showEducation ? '16px' : '24px'
        }}
      >
        <Info style={{ width: '18px', height: '18px' }} />
        {showEducation ? 'Hide' : 'Show'} why physical activity is important
      </button>

      {/* Education Panel */}
      {showEducation && (
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '15px' }}>
            Why physical activity is important (WHO)
          </h4>
          <p style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '14px', lineHeight: '1.6' }}>
            The World Health Organization says physical activity:
          </p>
          <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', color: '#475569', fontSize: '14px', lineHeight: '1.8' }}>
            <li>Keeps bones, muscles, and joints strong</li>
            <li>Helps reduce pain and stiffness</li>
            <li>Improves posture, balance, and movement</li>
            <li>Improves mood, sleep, and concentration</li>
            <li>Reduces the risk of heart disease, diabetes, and depression</li>
          </ul>
          <p style={{ 
            margin: 0, 
            padding: '12px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '8px',
            color: '#1e40af',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            💡 Any activity is better than none. More activity gives more health benefits.
          </p>

          {/* Safety message - shown if patient has reported pain */}
          {(formData.painSeverity && parseInt(formData.painSeverity) > 0) && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
                <strong>Movement is usually safe</strong>, even if you have pain. The WHO says physical activity 
                helps prevent and manage long-term pain. Start gently and build up slowly.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Goal Setting */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          Would you like to increase your activity a little?
        </label>
        {[
          { value: 'yes', label: 'Yes' },
          { value: 'not_now', label: 'Not right now' }
        ].map(option => (
          <label
            key={option.value}
            style={formData.exerciseGoalInterest === option.value ? radioStyleSelected : radioStyle}
          >
            <input
              type="radio"
              name="exerciseGoalInterest"
              value={option.value}
              checked={formData.exerciseGoalInterest === option.value}
              onChange={(e) => handleChange('exerciseGoalInterest', e.target.value)}
              style={{ marginRight: '12px', width: '20px', height: '20px' }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {/* Goal Selection - only show if interested */}
      {formData.exerciseGoalInterest === 'yes' && (
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>
            What feels realistic this week?
          </label>
          {[
            { value: 'add_5_10_min', label: 'Add 5-10 minutes of activity' },
            { value: 'add_1_day', label: 'Add 1 extra active day' },
            { value: 'break_up_sitting', label: 'Break up sitting time with movement' },
            { value: 'keep_same', label: 'Keep activity the same for now' }
          ].map(option => (
            <label
              key={option.value}
              style={formData.exerciseGoal === option.value ? radioStyleSelected : radioStyle}
            >
              <input
                type="radio"
                name="exerciseGoal"
                value={option.value}
                checked={formData.exerciseGoal === option.value}
                onChange={(e) => handleChange('exerciseGoal', e.target.value)}
                style={{ marginRight: '12px', width: '20px', height: '20px' }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}

      {/* Behaviour Change Message */}
      <div style={{
        backgroundColor: '#faf5ff',
        border: '1px solid #e9d5ff',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center'
      }}>
        <p style={{ margin: 0, color: '#7c3aed', fontSize: '15px', fontWeight: '500' }}>
          💪 Sit less. Move more. Even short movement breaks help your health.
        </p>
      </div>
    </div>
  );
};

export default ExerciseVitalSignSection;
